import { MARKETPLACE_DASHBOARDS_LIST_INITIAL_SIZE, useMarketplaceDashboards } from "@/api/hooks/marketplace";
import { CardGroup } from "@/components/molecules/CardGroup/CardGroup";
import { MarketplaceDashboardListItem } from "@/marketplace/marketplace-dashboard-list/MarketplaceDashboardListItem";
import "@/marketplace/marketplace-dashboard-list/marketplace-dashboard-list.scss";
import { buildAddDashboardErrorToast, buildAddDashboardSuccessToast, useMarketplaceToast } from "@/marketplace/toast";
import { pageMarketplaceDashboard, startsWithExtraPath } from "@/route";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { Button } from "@mm/metx-design-system";
import type { MarketplaceDashboardCard } from "@mm/metx-workbench.meteomatics.com";
import { useLocation } from "react-router-dom";

const MARKETPLACE_LOADING_PLACEHOLDERS = Array.from(
  { length: MARKETPLACE_DASHBOARDS_LIST_INITIAL_SIZE },
  (_, index) =>
    ({
      id: -(index + 1),
    }) as MarketplaceDashboardCard,
);

export function MarketplaceDashboardList() {
  const { i18n } = useLingui();
  const location = useLocation();
  const routePrefix = startsWithExtraPath(location.pathname);
  const { showToast, toast } = useMarketplaceToast();
  const { isLoading, isError, isEmpty, items, total, hasMore, isLoadingMore, loadMore, refetch } =
    useMarketplaceDashboards();
  const dashboards = isLoading ? MARKETPLACE_LOADING_PLACEHOLDERS : items;

  return (
    <section className="marketplace">
      {toast}
      <header className="marketplace__header">
        {!isLoading && !isEmpty && (
          <p className="marketplace__count">
            {i18n._({
              id: "marketplace.showing-count",
              message: "Showing {0} of {1} templates",
              values: { 0: items.length, 1: total },
            })}
          </p>
        )}
      </header>

      {isError ? (
        <div className="marketplace__error">
          <p className="marketplace__error-message">
            {t({
              id: "marketplace.load-error",
              message: "Could not load marketplace dashboards.",
            })}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            {t({ id: "marketplace.retry", message: "Try again" })}
          </Button>
        </div>
      ) : isEmpty ? (
        <p className="marketplace__empty">
          <Trans id="marketplace.empty">There are no dashboards available right now.</Trans>
        </p>
      ) : (
        <>
          <CardGroup layout="grid">
            {dashboards.map((dashboard) => (
              <MarketplaceDashboardListItem
                key={dashboard.id}
                dashboard={dashboard}
                loading={isLoading}
                detailHref={isLoading ? undefined : `${routePrefix}${pageMarketplaceDashboard.asString(dashboard.id)}`}
                onAddSuccess={(dashboardTitle, copiedProfile) =>
                  showToast(buildAddDashboardSuccessToast(dashboardTitle, copiedProfile, routePrefix))
                }
                onAddError={(description) => showToast(buildAddDashboardErrorToast(description))}
              />
            ))}
          </CardGroup>

          {!isEmpty && hasMore && (
            <div className="marketplace__footer">
              <Button variant="outline" disabled={isLoadingMore} onClick={loadMore}>
                {isLoadingMore ? <Trans>Loading...</Trans> : <Trans>Show more</Trans>}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
